<template>
  <v-app id="inspire">
    <v-card-title>
      Audio files
      <v-spacer></v-spacer>
      <v-dialog width="unset" height="unset" v-model="dialogs.upload">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            @click="
              uploading = false;
              updatingAudio = false;
            "
            class="ml-5"
            color="secondary"
            v-bind="attrs"
            v-on="on"
            >Upload</v-btn
          >
        </template>
        <v-card class="pa-2" background="white" width="400">
          <v-card-title> Upload audio file </v-card-title>
          <v-card-text>
            <v-file-input
              label="Click to browse..."
              accept=".mp3"
              filled
              show-size
              truncate-length="400"
              prepend-icon="mdi-paperclip"
              clear-icon="mdi-close"
              color="primary"
              v-model="file"
              @change="fileChanged"
            />
          </v-card-text>
          <v-text-field
            class="FileTextField"
            v-model="audioName"
            label="Name"
            single-line
            hide-details
          ></v-text-field>
          <v-text-field
            class="FileTextField"
            v-model="description"
            label="Description"
            single-line
            hide-details
          ></v-text-field>
          <v-combobox
            v-model="audioTags"
            label="Tags"
            variant="solo"
            chips
            clearable
            multiple
            class="FileTextField"
          >
          </v-combobox>
          <v-select
            v-model="selectedOwner"
            label="Select owner level"
            :items="getOwnerLevel()"
            class="FileTextField"
          ></v-select>
          <v-select
            v-model="selectedOrganization"
            label="Select organization"
            :items="getOrganizations"
            item-text="name"
            class="FileTextField"
            clearable
            persisten-hint
            hide-details
            return-object
          ></v-select>
          <v-card-actions>
            <v-btn
              @click="uploadAudio()"
              right
              :loading="uploading"
              :disabled="uploading || !validFileSpecified || !selectedOwner"
              color="primary"
            >
              Upload
              <v-icon right dark>mdi-cloud-upload</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :search="search"
      :items="getAudioFiles"
      :hide-default-footer="true"
      disable-pagination
      item-key="audioFile.id"
    >
      <!-- ACTIONS -->
      <template v-slot:item.actions="{ item }">
        <!-- DELETE and EDit Collection BUTTON-->
        <v-btn icon @click="openEditDialog(item)" color="secondary"
          ><v-icon>mdi-pencil</v-icon></v-btn
        >
        <v-btn icon @click="deleteAudioFile(item.id)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <c-confirmation-dialog ref="confirmationDialog"></c-confirmation-dialog>
  </v-app>
</template>

<script>
import {
  ADD_OR_UPDATE_AUDIO_FILES,
  GET_AUDIO_FILES_QUERY,
  DELETE_AUDIO_FILES,
} from '../../graphql/shootingProgramQueries.js';
import { GET_ORGANIZATIONS } from '../../graphql/organizationQueries';
import { toBase64, OWNER_LEVEL } from '../../utils/Helper.js';

export default {
  components: {
    CConfirmationDialog: () => import('../common/CConfirmationDialog'),
  },
  name: 'AudioFiles',
  data: () => ({
    selectedOrganization: {},
    audioTags: [],
    audioName: '',
    description: '',
    selectedOwner: null,
    uploading: false,
    updatingAudio: false,
    search: '',
    selectedLanguage: {},
    file: {}, // The file that the user selects/uploads from file input
    base64File: null, // If editing an audio file, this will be the already encoded file from the database
    expanded: [],
    dialogs: {
      upload: false,
    },
    testArr: [{ name: 'Norwegian' }, { name: 'English' }],
    headers: [
      { text: 'Name', value: 'jsonData.name' },
      { text: 'Description', value: 'jsonData.description' },
      { text: 'Version', value: 'jsonData.version' },
      {
        text: '',
        value: 'actions',
        sortable: false,
        align: 'right',
      },
    ],
  }),
  created() {},
  apollo: {
    $client: 'shootingProgramClient',
    getAudioFiles: {
      query: GET_AUDIO_FILES_QUERY,
      client: 'shootingProgramClient',
      loadingKey: 'loading',
      options: { fetchPolicy: 'cache-and-network' },
    },
    getOrganizations: {
      client: 'organizationClient',
      query: GET_ORGANIZATIONS,
      loadingKey: 'loading',
    },
  },

  computed: {
    validFileSpecified() {
      if (this.file === undefined) {
        return false;
      }

      return this.file.name !== undefined && this.file.size > 0;
    },
  },
  methods: {
    fileChanged() {
      this.base64File = null;
    },
    getOwnerLevel() {
      return OWNER_LEVEL;
    },
    refetchAudioFiles() {
      this.$apollo.queries.getAudioFiles.refetch();
    },
    async deleteAudioFile(id) {
      if (
        await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this audio file?'
        )
      ) {
        try {
          await this.$apollo.mutate({
            mutation: DELETE_AUDIO_FILES,
            variables: {
              id: id,
            },
          });
        } catch (err) {
          alert('Error deleting audio file! ' + err.message);
        }
        this.refetchAudioFiles();
      }
    },
    async uploadAudio() {
      if (this.validFileSpecified) {
        try {
          // Set uploading state to indicate that the upload process is ongoing
          this.uploading = true;

          let audioFile;
          if (this.base64File) {
            audioFile = this.base64File;
          } else {
            audioFile = await toBase64(this.file);
          }

          const validBase64 = audioFile.replace(/^data:.+;base64,/, ''); // Remove data URI scheme

          let id = null;
          if (this.audioName === '')
            this.audioName = this.file.name.split('.')[0];

          if (this.updatingAudio) {
            id = this.audioId;
          }
          await this.$apollo.mutate({
            mutation: ADD_OR_UPDATE_AUDIO_FILES,
            variables: {
              id: id,
              audioFile: {
                file: validBase64,
                jsonData: {
                  name: this.audioName,
                  description: this.description,
                  organizationId: this.selectedOrganization?.id,
                  owner: {
                    level: this.selectedOwner,
                  },
                  tags: this.audioTags,
                },
              },
            },
          });

          this.uploading = false;
          this.file = {};
          this.base64File = null;
          this.description = '';
          this.selectedOwner = null;
          this.audioName = '';
          this.selectedOrganization = {};
          this.audioTags = [];
          this.updatingAudio = false;

          this.dialogs.upload = false;

          this.refetchAudioFiles();
        } catch (error) {
          console.error(error);
          this.uploading = false;
        }
      }
    },
    openEditDialog(audioFile) {
      const org = this.getOrganizations?.filter(
        (g) => g.id == audioFile.jsonData.organizationId
      );
      this.audioId = audioFile.id;
      this.file = new Blob([audioFile.file], { type: 'audio/mpeg' }); // MIME type for mp3 files is audio/mpeg
      this.base64File = audioFile.file;
      this.file.name = audioFile.jsonData.name;

      this.description = audioFile.jsonData.description;
      this.selectedOwner = audioFile.jsonData.owner.level;
      this.audioName = audioFile.jsonData.name;
            if(org)
      this.selectedOrganization = org.length != 0 ? org[0] : null;
      this.audioTags = audioFile.jsonData.tags;
      this.updatingAudio = true;
      this.dialogs.upload = true;
    },
  },
};
</script>

<style scoped>
.FileTextField {
  margin: 10px;
}
</style>
